let cartDrawerIconBound = false;
let cartDrawerPageshowBound = false;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.bindHeaderCartIcon();
    this.bindPageshowSync();
    this.initDiscountForms();
  }

  /** Document-level delegation so the drawer still opens after checkout/bfcache/re-renders */
  bindHeaderCartIcon() {
    this.syncHeaderCartIconAttrs();

    if (cartDrawerIconBound) return;
    cartDrawerIconBound = true;

    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest('#cart-icon-bubble');
      if (!cartLink) return;

      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      event.preventDefault();
      drawer.open(cartLink);
    });

    document.addEventListener('keydown', (event) => {
      const cartLink = event.target.closest('#cart-icon-bubble');
      if (!cartLink) return;
      if (event.code.toUpperCase() !== 'SPACE') return;

      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      event.preventDefault();
      drawer.open(cartLink);
    });
  }

  syncHeaderCartIconAttrs() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  /** Re-sync drawer UI from server after back/forward cache (does not clear the cart) */
  bindPageshowSync() {
    if (cartDrawerPageshowBound) return;
    cartDrawerPageshowBound = true;

    window.addEventListener('pageshow', (event) => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      drawer.syncHeaderCartIconAttrs();

      if (event.persisted) {
        drawer.syncCartUiFromServer();
      }
    });
  }

  async syncCartUiFromServer() {
    try {
      const response = await fetch(
        `${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`,
      );
      if (!response.ok) return;

      const sections = await response.json();

      if (sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(
          sections['cart-drawer'],
          'text/html',
        );
        const sourceDrawer = doc.querySelector('cart-drawer');
        if (sourceDrawer) {
          this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));

          const sourceInner = sourceDrawer.querySelector('#CartDrawer');
          const targetInner = this.querySelector('#CartDrawer');
          if (sourceInner && targetInner) {
            targetInner.innerHTML = sourceInner.innerHTML;
          }
        }
      }

      if (sections['cart-icon-bubble']) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          const doc = new DOMParser().parseFromString(
            sections['cart-icon-bubble'],
            'text/html',
          );
          const sectionEl = doc.querySelector('.shopify-section') || doc.body;
          bubble.innerHTML = sectionEl.innerHTML;
        }
      }

      this.querySelector('#CartDrawer-Overlay')?.addEventListener(
        'click',
        this.close.bind(this),
      );
      this.syncHeaderCartIconAttrs();
      this.initDiscountForms();
    } catch (error) {
      console.error('[cart-drawer] Failed to sync after pageshow:', error);
    }
  }

  initDiscountForms() {
    this.querySelectorAll('[data-cart-drawer-discount]').forEach((root) => {
      if (root.dataset.discountBound === 'true') return;
      root.dataset.discountBound = 'true';

      const input = root.querySelector('[data-discount-input]');
      const submit = root.querySelector('[data-discount-submit]');
      const errorEl = root.querySelector('[data-discount-error-message]');
      const labelEl = submit?.querySelector('[data-discount-submit-label]');
      const spinnerEl = submit?.querySelector('[data-discount-spinner]');

      if (!input || !submit) return;

      const updateSubmitState = () => {
        submit.disabled = !input.value.trim();
      };

      input.addEventListener('input', updateSubmitState);
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submit.click();
        }
      });

      submit.addEventListener('click', () => this.applyDiscountCode(root, input, submit, errorEl, labelEl, spinnerEl));

      root.querySelectorAll('[data-discount-remove]').forEach((button) => {
        button.addEventListener('click', () => this.removeDiscountCode(root, input, submit, errorEl, labelEl, spinnerEl));
      });

      updateSubmitState();
    });
  }

  setDiscountLoading(input, submit, labelEl, spinnerEl, isLoading) {
    input.disabled = isLoading;
    if (labelEl) labelEl.hidden = isLoading;
    if (spinnerEl) spinnerEl.hidden = !isLoading;
    if (!isLoading) submit.disabled = !input.value.trim();
  }

  async applyDiscountCode(root, input, submit, errorEl, labelEl, spinnerEl) {
    const code = input.value.trim();
    if (!code) return;

    await this.updateCartDiscount(code, root, input, submit, errorEl, labelEl, spinnerEl, true);
  }

  async removeDiscountCode(root, input, submit, errorEl, labelEl, spinnerEl) {
    await this.updateCartDiscount('', root, input, submit, errorEl, labelEl, spinnerEl, false);
  }

  async updateCartDiscount(discountCode, root, input, submit, errorEl, labelEl, spinnerEl, showInvalidError) {
    this.setDiscountLoading(input, submit, labelEl, spinnerEl, true);

    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    try {
      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: discountCode,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (data.sections?.['cart-drawer']) {
        this.renderContents(data);
        return;
      }

      await this.refreshDrawerFromSection();

      if (
        showInvalidError &&
        discountCode &&
        !(data.cart_level_discount_applications && data.cart_level_discount_applications.length > 0)
      ) {
        if (errorEl) {
          errorEl.textContent = root.dataset.discountError;
          errorEl.hidden = false;
        }
      }
    } catch (error) {
      console.error(error);
      if (errorEl) {
        errorEl.textContent = root.dataset.discountError;
        errorEl.hidden = false;
      }
    } finally {
      this.setDiscountLoading(input, submit, labelEl, spinnerEl, false);
    }
  }

  async refreshDrawerFromSection() {
    const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const sourceDrawer = doc.querySelector('cart-drawer');
    if (sourceDrawer) {
      this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
    }

    const sourceItems = doc.querySelector('cart-drawer-items');
    const sourceFooter = doc.querySelector('.drawer__footer');
    const targetItems = this.querySelector('cart-drawer-items');
    const targetFooter = this.querySelector('.drawer__footer');

    if (sourceItems && targetItems) {
      targetItems.replaceWith(sourceItems);
    }

    if (sourceFooter && targetFooter) {
      targetFooter.replaceWith(sourceFooter);
    }

    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.initDiscountForms();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.initDiscountForms();
    this.syncHeaderCartIconAttrs();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
